import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import type WaveSurfer from 'wavesurfer.js';
import { cn } from './cn.js';
import { Icon } from './Icon.js';

/**
 * O player de áudio da casa — um só, e o mesmo desenho em todo navegador.
 *
 * ## Por que ele existe
 *
 * `<audio controls>` desenha uma barra DIFERENTE em cada navegador: o Chrome
 * põe uma pílula cinza com o menu de três pontos, o Safari uma barra clara com
 * cantos próprios, o Firefox uma terceira coisa. Nenhuma delas conhece os
 * tokens da marca, nenhuma escurece com o tema, e as três mudam de altura entre
 * si — dentro do mesmo cartão, o mesmo anexo ficava com três alturas conforme
 * quem abrisse. É o defeito que o `DESIGN.md` chama de "parecer de outro
 * produto", com a agravante de não haver uma linha nossa para corrigir.
 *
 * Aqui o desenho é nosso inteiro: a onda é canvas (WaveSurfer), e o resto —
 * tocar, tempo, volume, velocidade — são botões da casa. A régua `audio-cru`
 * (`tools/design/check.mjs`) derruba a build de quem escrever `<audio>` em
 * outro arquivo; a exceção é este, e é o caso de falha lá embaixo.
 *
 * ## A cor da onda não pode ser escrita aqui
 *
 * Canvas não lê classe do Tailwind: o WaveSurfer quer uma cor CSS resolvida, em
 * string. Escrevê-la à mão daria o defeito que o `DESIGN.md` proíbe em toda
 * linha — a peça teria a própria paleta, não escureceria com o tema e não
 * mudaria no portal, que redefine 29 tokens.
 *
 * Então ela é LIDA do tema, e por um desvio que vale explicar:
 * `getComputedStyle(el).getPropertyValue('--cor-primaria')` devolve o texto
 * declarado — o `light-dark(…, …)` inteiro —, que o canvas não sabe pintar.
 * Quem resolve o par claro/escuro é a propriedade `color` de um elemento de
 * verdade, e é isso que `waveColors` faz: um `<span>` invisível dentro do
 * player, pintado com o token, e o valor que volta já é a cor do tema em vigor,
 * na paleta da aplicação que montou a tela.
 *
 * E ela é relida quando o tema troca — `data-tema` no `<html>` ou o
 * `prefers-color-scheme` do sistema. Sem isso, a onda ficava com a cor de
 * quando o componente montou: o resto da tela escurecia e ela não.
 *
 * ## O que ele NÃO tenta ser
 *
 * Editor. Não corta, não marca região, não mostra espectrograma. O áudio aqui é
 * anexo de demanda — o que se faz com ele é ouvir, voltar cinco segundos e
 * acelerar quem fala devagar.
 */

/** As velocidades do ciclo, na ordem em que o botão as percorre. */
const RATES = [1, 1.25, 1.5, 2] as const;

/** A próxima velocidade do ciclo — e volta a 1× depois da última. */
export function nextRate(current: number): number {
  const at = RATES.indexOf(current as (typeof RATES)[number]);
  return RATES[(at + 1) % RATES.length] ?? 1;
}

/**
 * O relógio do player: `0:07`, `4:32`, `1:02:09`.
 *
 * A hora só aparece quando existe — `0:00:07` num áudio de sete segundos ocupa
 * a largura de um de uma hora e não diz nada a mais. E o que não é número vira
 * `0:00` em vez de `NaN:aN`, que é o que sai da conta crua enquanto a duração
 * ainda não foi decodificada.
 */
export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const total = Math.floor(seconds);
  const pad = (value: number): string => String(value).padStart(2, '0');
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${minutes}:${pad(rest)}`;
}

/** Onde o ponteiro caiu dentro de uma trilha, de 0 a 1 — sempre dentro dela. */
export function ratioAt(clientX: number, box: { left: number; width: number }): number {
  if (box.width <= 0) return 0;
  return Math.min(1, Math.max(0, (clientX - box.left) / box.width));
}

/**
 * As três cores da onda, resolvidas pelo tema em vigor.
 *
 * `borda-forte` na onda e não `borda`: a onda é o contorno de um controle, e a
 * WCAG 1.4.11 cobra 3:1 dela contra a superfície — que é exatamente a promessa
 * daquele token e não a do outro. O progresso é a primária, e o cursor a
 * primária um passo mais forte, que é o par que o botão de contorno usa para
 * separar borda de rótulo sem inventar uma terceira cor.
 */
function waveColors(host: HTMLElement): {
  waveColor: string;
  progressColor: string;
  cursorColor: string;
} {
  const probe = document.createElement('span');
  probe.style.display = 'none';
  host.append(probe);
  const read = (token: string): string => {
    probe.style.color = `var(${token})`;
    return getComputedStyle(probe).color;
  };
  const colors = {
    waveColor: read('--cor-borda-forte'),
    progressColor: read('--cor-primaria-texto'),
    cursorColor: read('--cor-primaria-forte'),
  };
  probe.remove();
  return colors;
}

/**
 * Se o navegador deixa mesmo mudar o volume por código.
 *
 * No iOS não deixa: o volume do elemento de mídia é somente-leitura ali por
 * decisão da plataforma — quem manda nele é o botão físico. A atribuição não
 * lança, não avisa e não faz nada, então um slider desenhado lá é um controle
 * que se move e não muda o som, que é a definição de tela afirmando o que o
 * código não cumpre. Onde ele não vale, o player mostra só o botão de mudo, que
 * funciona em toda parte.
 */
function volumeIsWritable(): boolean {
  if (typeof Audio === 'undefined') return false;
  try {
    const probe = new Audio();
    probe.volume = 0.5;
    return probe.volume === 0.5;
  } catch {
    return false;
  }
}

export interface AudioPlayerProps {
  /** A URL do áudio: assinada, `blob:` de arquivo recém-escolhido ou pública. */
  src: string;
  /** O que o leitor de tela anuncia — o nome do arquivo, quase sempre. */
  label: string;
  className?: string;
}

export function AudioPlayer({ src, label, className }: AudioPlayerProps) {
  const host = useRef<HTMLDivElement>(null);
  const wave = useRef<WaveSurfer | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState<number>(1);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showVolume] = useState(volumeIsWritable);

  /*
   * O WaveSurfer entra por `import()` e não pelo topo do arquivo.
   *
   * São 40 kB — 12 kB depois do gzip, medidos no `dist` da interna em 18/09 —
   * para o áudio de UMA tela. Importado estático, eles viajam no pacote
   * principal das duas SPAs, inclusive no do portal, que é o que abre no
   * telefone do associado. Dinâmico, o Vite o põe num pedaço à parte, e quem
   * nunca abre um áudio nunca o baixa.
   */
  useEffect(() => {
    const container = host.current;
    if (!container) return;

    let alive = true;
    let instance: WaveSurfer | null = null;
    setReady(false);
    setFailed(false);
    setPlaying(false);
    setElapsed(0);
    setDuration(0);

    void import('wavesurfer.js')
      .then((lib) => {
        if (!alive) return;
        instance = lib.default.create({
          container,
          url: src,
          height: 42,
          barWidth: 3,
          barGap: 2,
          barRadius: 2,
          cursorWidth: 2,
          normalize: true,
          dragToSeek: true,
          ...waveColors(container),
        });
        wave.current = instance;
        instance.on('ready', (seconds) => {
          setDuration(seconds);
          setReady(true);
        });
        instance.on('timeupdate', setElapsed);
        instance.on('play', () => setPlaying(true));
        instance.on('pause', () => setPlaying(false));
        instance.on('finish', () => setPlaying(false));
        instance.on('error', () => setFailed(true));
      })
      .catch(() => {
        if (alive) setFailed(true);
      });

    return () => {
      alive = false;
      wave.current = null;
      /*
       * `destroy` durante o carregamento aborta o pedido da onda, e o aborto
       * volta como exceção — em desenvolvimento isso derruba a tela inteira no
       * `StrictMode`, que monta e desmonta todo efeito uma vez de propósito.
       */
      try {
        instance?.destroy();
      } catch {
        // desmontando: não há a quem contar, e não há o que consertar
      }
    };
  }, [src]);

  /* O tema trocou: a onda relê as cores em vez de ficar com as de quando montou. */
  useEffect(() => {
    const repaint = (): void => {
      const container = host.current;
      if (container && wave.current) wave.current.setOptions(waveColors(container));
    };
    const system = matchMedia('(prefers-color-scheme: dark)');
    const watcher = new MutationObserver(repaint);
    watcher.observe(document.documentElement, { attributeFilter: ['data-tema'] });
    system.addEventListener('change', repaint);
    return () => {
      watcher.disconnect();
      system.removeEventListener('change', repaint);
    };
  }, []);

  useEffect(() => {
    wave.current?.setVolume(volume);
    wave.current?.setMuted(muted);
  }, [volume, muted, ready]);

  useEffect(() => {
    wave.current?.setPlaybackRate(rate, true);
  }, [rate, ready]);

  const toggle = useCallback(() => {
    void wave.current?.playPause();
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const instance = wave.current;
    if (!instance || !ready) return;
    const jumpTo = (seconds: number): void => {
      event.preventDefault();
      instance.setTime(Math.min(duration, Math.max(0, seconds)));
    };
    if (event.key === 'ArrowRight') jumpTo(instance.getCurrentTime() + 5);
    else if (event.key === 'ArrowLeft') jumpTo(instance.getCurrentTime() - 5);
    else if (event.key === 'Home') jumpTo(0);
    else if (event.key === 'End') jumpTo(duration);
    else if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      toggle();
    }
  };

  const shell = cn(
    'flex min-h-18 items-center gap-4 rounded-grande border border-borda bg-superficie px-4 py-3',
    className,
  );

  /*
   * O caso de falha, e ele é o motivo de este arquivo ser a exceção da régua.
   *
   * A onda precisa BAIXAR e decodificar o arquivo inteiro; tocar não precisa —
   * o elemento de mídia toca por partes, e é o que sobra quando o CORS barra o
   * pedido ou o codec não decodifica. Trocar um áudio que toca por uma mensagem
   * de erro seria perder o que ainda funciona para preservar o desenho.
   */
  if (failed) {
    return (
      <div className={cn(shell, 'flex-col items-stretch gap-2')}>
        <p className="text-xs text-texto-suave">
          Não foi possível desenhar a onda deste áudio. Ele continua tocando pelo controle
          do navegador.
        </p>
        <audio src={src} controls className="w-full" aria-label={label} />
      </div>
    );
  }

  return (
    <div className={shell} aria-busy={!ready}>
      <button
        type="button"
        onClick={toggle}
        disabled={!ready}
        aria-label={playing ? `Pausar ${label}` : `Tocar ${label}`}
        className={cn(
          'acao-primaria grid size-12 shrink-0 cursor-pointer place-items-center',
          'rounded-pilula bg-primaria text-primaria-contraste transition-[background-color]',
          'hover:bg-primaria-forte active:scale-[0.98]',
          'disabled:pointer-events-none disabled:opacity-50',
        )}
      >
        <Icon nome={playing ? 'pausa' : 'tocar'} size={20} />
      </button>

      <div
        role="slider"
        tabIndex={0}
        aria-label={`Posição de ${label}`}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(elapsed)}
        aria-valuetext={`${formatClock(elapsed)} de ${formatClock(duration)}`}
        onKeyDown={onKeyDown}
        className="min-w-0 flex-1 cursor-pointer rounded-pequeno"
      >
        <div ref={host} className="w-full" />
      </div>

      {/*
       * A onda é a única peça elástica da fileira, então tudo que divide a
       * fileira com ela precisa ter largura FIXA — senão ela encolhe e cresce
       * sozinha, no meio da reprodução, e o desenho da onda muda de escala com
       * um relógio andando. Eram duas as peças que mudavam:
       *
       * - o **decorrido**, que ganha um dígito ao passar de `9:59` para
       *   `10:00`. Quem reserva o espaço dele é a duração desenhada por baixo,
       *   invisível: o decorrido nunca passa dela, então a largura é decidida
       *   uma vez, quando o arquivo termina de carregar, e não muda mais.
       *   Reservar por `min-w-` seria um número escolhido a olho, errado para o
       *   áudio de uma hora e sobrando para o de sete segundos.
       * - a **velocidade**, que vai de `2×` a `1,25×`. Ali a largura é fixa
       *   mesmo, porque as quatro possibilidades são conhecidas.
       */}
      <p className="numero shrink-0 text-xs font-medium text-texto-suave">
        {ready ? (
          <span className="relative inline-block text-right">
            <span aria-hidden className="invisible">
              {formatClock(duration)}
            </span>
            <span className="absolute inset-0 text-right">{formatClock(elapsed)}</span>
          </span>
        ) : null}
        {ready ? ` / ${formatClock(duration)}` : 'Carregando…'}
      </p>

      <div className="hidden shrink-0 items-center gap-2 sm:flex">
        <button
          type="button"
          onClick={() => setMuted(!muted)}
          aria-label={muted ? 'Tirar o mudo' : 'Deixar mudo'}
          className={cn(
            'grid size-8 cursor-pointer place-items-center rounded-borda text-texto-suave',
            'transition-colors hover:bg-superficie-suave hover:text-texto',
            'pointer-coarse:size-11',
          )}
        >
          <Icon nome={muted ? 'mudo' : 'som'} size={20} />
        </button>
        {showVolume ? (
          <VolumeTrack
            value={muted ? 0 : volume}
            onChange={(next) => {
              setMuted(next === 0);
              setVolume(next);
            }}
          />
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => setRate(nextRate(rate))}
        aria-label={`Velocidade ${rate.toLocaleString('pt-BR')}×. Trocar.`}
        className={cn(
          'numero grid h-8 w-16 shrink-0 cursor-pointer place-items-center',
          'rounded-pilula border border-borda bg-superficie-suave transition-colors',
          'text-xs font-semibold text-texto-suave',
          'hover:border-borda-forte hover:text-texto pointer-coarse:h-11',
        )}
      >
        {rate.toLocaleString('pt-BR')}×
      </button>
    </div>
  );
}

/**
 * A trilha do volume, escrita à mão — e não um `<input type="range">`.
 *
 * O `range` é a peça que mais diverge entre navegadores: polegar redondo no
 * Chrome, quadrado no Firefox, altura de trilha própria em cada um, e
 * `accent-color` só pinta o que o navegador já desenha do jeito dele. Um player
 * que existe para ter o MESMO desenho em toda parte não pode ter a peça mais
 * divergente da plataforma dentro.
 *
 * O que ela não abre mão por ser nossa: papel de slider com o valor anunciado,
 * foco visível, setas do teclado, e 44px de alvo no dedo — a trilha desenhada
 * continua com 4px, o que cresce é a área que responde ao toque.
 */
function VolumeTrack({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  const track = useRef<HTMLDivElement>(null);

  const seek = (clientX: number): void => {
    const box = track.current?.getBoundingClientRect();
    if (box) onChange(ratioAt(clientX, box));
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    seek(event.clientX);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) seek(event.clientX);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = (amount: number): void => {
      event.preventDefault();
      onChange(Math.min(1, Math.max(0, Number((value + amount).toFixed(2)))));
    };
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') step(0.1);
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') step(-0.1);
    else if (event.key === 'Home') step(-1);
    else if (event.key === 'End') step(1);
  };

  const percent = Math.round(value * 100);

  return (
    <div
      ref={track}
      role="slider"
      tabIndex={0}
      aria-label="Volume"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-valuetext={`${percent}%`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onKeyDown={onKeyDown}
      className="relative flex h-6 w-18 cursor-pointer touch-none items-center pointer-coarse:h-11"
    >
      <span aria-hidden className="h-1 w-full rounded-pilula bg-borda" />
      <span
        aria-hidden
        className="absolute h-1 rounded-pilula bg-primaria-texto"
        style={{ width: `${percent}%` }}
      />
      <span
        aria-hidden
        className="absolute size-2.5 -translate-x-1/2 rounded-pilula bg-primaria-texto"
        style={{ left: `${percent}%` }}
      />
    </div>
  );
}
